import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from './Icon';
import '../styles/components/LoginPage.css';

const LoginPage: React.FC = () => {
  const { login, loginWithPassword, loginDemo, isLoading, sessionEnded } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [mode, setMode] = useState<'choose' | 'password'>('choose');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demoLoading, setDemoLoading] = useState(false);

  const redirectPath = `${location.pathname}${location.search}${location.hash}`;

  const handleGoogle = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await login(redirectPath);
    } catch (err: any) {
      setError(err?.message || 'Sign in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // The demo is never probed on load: the only request is the one this click makes. A 404 (demo switched
  // off) or any other failure is a plain message the person can read again; there is no retry loop.
  const handleDemo = async () => {
    if (demoLoading) return;
    setError(null);
    setDemoLoading(true);
    try {
      await loginDemo();
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'The demo is not available right now.');
      setDemoLoading(false);
    }
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError(null);
    setSubmitting(true);
    try {
      await loginWithPassword(email.trim(), password);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Invalid email or password.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">
            <Icon name="sparkles" size={40} />
          </div>
          <h1 className="login-title">Anojeje</h1>
          <p className="login-subtitle">Your planning companion</p>
        </div>

        <div className="login-divider" />

        {sessionEnded && (
          <div className="login-notice" role="status">
            Your session has ended. Please sign in again to carry on where you left off.
          </div>
        )}

        {/* Always in the page so a screen reader announces the message when it appears. */}
        <div aria-live="polite" aria-atomic="true">
          {error && <div className="login-error">{error}</div>}
        </div>

        {mode === 'choose' && (
          <>
            <p className="login-description">Sign in to manage your events, budget, vendors, and more.</p>

            <button
              className="google-login-button"
              onClick={handleGoogle}
              disabled={isLoading || submitting || demoLoading}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" className="google-icon">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {(isLoading || submitting) ? 'Signing in…' : 'Continue with Google'}
            </button>

            <div className="login-or">
              <span>or</span>
            </div>

            <button
              className="login-email-button"
              onClick={() => { setError(null); setMode('password'); }}
            >
              Sign in with email
            </button>

            <button
              type="button"
              className="login-email-button login-demo-button"
              onClick={handleDemo}
              disabled={isLoading || submitting || demoLoading}
              aria-busy={demoLoading}
            >
              {demoLoading ? 'Opening the demo…' : 'Try the demo'}
            </button>
          </>
        )}

        {mode === 'password' && (
          <form className="login-form" onSubmit={handlePassword}>
            <div className="login-field">
              <label className="login-label" htmlFor="login-email">Email</label>
              <input
                id="login-email"
                className="login-input"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="login-field">
              <label className="login-label" htmlFor="login-password">Password</label>
              <input
                id="login-password"
                className="login-input"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              className="login-submit-button"
              disabled={submitting || !email.trim() || !password}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
            <button
              type="button"
              className="login-back-link"
              onClick={() => { setError(null); setMode('choose'); }}
            >
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
